import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Checkbox,
  TextField,
  Button,
  MenuItem,
  Select,
  styled,
  useMediaQuery,
  InputAdornment,
  CircularProgress,
  Tooltip,
  Pagination,
  FormControl,
  Tabs,
  Tab,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useDispatch, useSelector } from "react-redux";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import SearchIcon from "@mui/icons-material/Search";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { IconButton } from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format } from "date-fns";
import JSZip from "jszip";

import { AppDispatch, RootState } from "../../stores";

import {
  downloadReport,
  ReportDetails,
  WhiteLabelConfig,
} from "../../utils/downloadReport";

// Import add white label actions and types
import { getMyAddWhiteLabel } from "../../features/add-white-label-module/addWhiteLabelAction";

// Import site report actions and types
import {
  getPastGeneratedReports,
  updateReportNameById,
} from "../../features/generate-report-module/reportAction";

// Import fleet report actions and types
import {
  getPastFleetReports,
  updateFleetReportNameById,
} from "../../features/fleet-report-module/fleetReportAction";
import {
  downloadFleetReport,
  FleetReportDetails,
} from "../../utils/downloadFleetReport";

const Container = styled(Box)(({ theme }) => ({
  minHeight: "90vh",
  background: "#fff",
  display: "flex",
  flexDirection: "row",
  overflow: "hidden",
  transform: "scale(0.95)",
  transformOrigin: "center",
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
  },
  [theme.breakpoints.down("sm")]: {
    minHeight: "85vh",
  },
}));

const Header = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: theme.spacing(3),
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
    alignItems: "flex-start",
    gap: theme.spacing(2),
  },
}));

const SearchInput = styled(TextField)(({ theme }) => ({
  width: "200px",
  backgroundColor: "#f8f8f8",
  borderRadius: 5,
  "& .MuiOutlinedInput-root": {
    borderRadius: 8,
  },
  [theme.breakpoints.down("md")]: {
    width: "200px",
  },
  [theme.breakpoints.down("sm")]: {
    width: "100%",
  },
  "& .MuiOutlinedInput-input": {
    padding: "7px 12px",
    fontSize: "0.85rem",
    [theme.breakpoints.down("sm")]: {
      padding: "10px 12px",
      fontSize: "0.8rem",
    },
  },
}));

const ExportButton = styled(Button)(({ theme }) => ({
  textTransform: "none",
  fontWeight: 600,
  background: "linear-gradient(to top, #758d8e, #4b5b5d)",
  color: "#fff",
  borderRadius: 8,
  padding: theme.spacing(0.9, 2),
  marginLeft: theme.spacing(2),
  "&:hover": {
    backgroundColor: "#354142",
  },
  "&.Mui-disabled": {
    backgroundColor: "#bdbdbd",
  },
  [theme.breakpoints.down("md")]: {
    marginLeft: theme.spacing(1),
  },
  [theme.breakpoints.down("sm")]: {
    width: "100%",
    marginLeft: 0,
  },
}));

const StyledTable = styled(Table)(({ theme }) => ({
  minWidth: 650,
  width: "100%",
  tableLayout: "auto",
  [theme.breakpoints.down("md")]: {
    minWidth: 600,
  },
  [theme.breakpoints.down("sm")]: {
    minWidth: 500,
  },
}));

const WelcomeText = styled(Typography)(({ theme }) => ({
  fontSize: "28px",
  fontWeight: 600,
  color: "#4b5b5d",
  textAlign: "left",
  [theme.breakpoints.down("sm")]: {
    fontSize: "20px",
  },
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontSize: "0.8rem",
  color: "#4b5b5d",
  fontWeight: 500,
  padding: theme.spacing(0.3),
  whiteSpace: "nowrap",
  [theme.breakpoints.down("sm")]: {
    fontSize: "0.75rem",
    padding: theme.spacing(0.3),
  },
}));

const StyledTableHeadCell = styled(StyledTableCell)(({ theme }) => ({
  backgroundColor: "#f5f0e7",
  fontWeight: 600,
  color: "#4b5b5d",
  padding: theme.spacing(0.5),
  lineHeight: "1rem",
  whiteSpace: "nowrap",
  [theme.breakpoints.down("sm")]: {
    fontSize: "0.85rem",
    padding: theme.spacing(2),
    lineHeight: "0.5rem",
  },
}));

const DownloadSelect = styled(Select)(({ theme }) => ({
  fontSize: "0.65rem",
  height: "22px",
  borderRadius: "6px",
  backgroundColor: "#f8f8f8",
  "& .MuiSelect-select": {
    padding: "3px 8px",
    display: "flex",
    alignItems: "center",
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "#e0e0e0",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "#4b5b5d",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "#4b5b5d",
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: "0.6rem",
    height: "20px",
    "& .MuiSelect-select": {
      padding: "2px 6px",
    },
  },
}));

const FooterContainer = styled(Box)(({ theme }) => ({
  position: "fixed",
  bottom: 0,
  left: 0,
  right: 0,
  background: "#fff",
  padding: theme.spacing(2),
  zIndex: 1000,
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  flexWrap: "wrap",
  gap: theme.spacing(2),
  [theme.breakpoints.down("sm")]: {
    flexDirection: "column",
    padding: theme.spacing(1),
  },
}));

const DatePickerWrapper = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  backgroundColor: "#f8f8f8",
  borderRadius: 8,
  padding: theme.spacing(1),
  "& .react-datepicker-wrapper": {
    width: "180px",
  },
  "& .react-datepicker__input-container input": {
    backgroundColor: "transparent",
    border: "1px solid #e0e0e0",
    borderRadius: "6px",
    padding: "7px",
    fontSize: "0.75rem",
    width: "100%",
    outline: "none",
    "&:focus": {
      borderColor: "#4b5b5d",
      boxShadow: "0 0 0 2px rgba(75, 91, 93, 0.2)",
    },
  },
  [theme.breakpoints.down("md")]: {
    "& .react-datepicker-wrapper": {
      width: "120px",
    },
    "& .react-datepicker__input-container input": {
      fontSize: "0.85rem",
      padding: "6px",
    },
  },
  [theme.breakpoints.down("sm")]: {
    width: "100%",
    "& .react-datepicker-wrapper": {
      width: "100%",
    },
  },
}));

const LoaderContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  padding: theme.spacing(6),
  minHeight: "300px",
}));

const LoaderText = styled(Typography)(({ theme }) => ({
  marginTop: theme.spacing(2),
  fontSize: "0.9rem",
  color: "#4b5b5d",
  fontWeight: 500,
}));

const formatDateToYYYYMMDD = (date: Date | null): string => {
  if (!date) return "";
  return format(date, "yyyy:MM:dd");
};

// Tab Panel Component
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`reports-tabpanel-${index}`}
      aria-labelledby={`reports-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
};

const PastReports = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  // Site Estimator Reports state
  const { reports, loading, error, totalPages, totalReports } = useSelector(
    (state: RootState) => state.report
  );

  // Fleet Estimator Reports state
  const {
    reports: fleetReports,
    loading: fleetLoading,
    error: fleetError,
    totalReports: fleetTotalReports,
    totalPages: fleetTotalPages,
  } = useSelector((state: RootState) => state.fleetReport);

  const { whiteLabel, loading: whiteLabelLoading } = useSelector(
    (state: RootState) => state.addWhiteLabel
  );

  const userData = JSON.parse(localStorage.getItem("user") || "{}");
  const currentPlanName = (
    userData?.subscription?.planName || ""
  ).toLowerCase();
  const isStarterPlan = currentPlanName === "core";

  // Site Estimator state
  const [selectedReports, setSelectedReports] = useState<string[]>([]);
  const [editingReportId, setEditingReportId] = useState<string | null>(null);
  const [editingReportName, setEditingReportName] = useState("");

  // Fleet Estimator state
  const [selectedFleetReports, setSelectedFleetReports] = useState<string[]>(
    []
  );
  const [editingFleetReportId, setEditingFleetReportId] = useState<
    string | null
  >(null);
  const [editingFleetReportName, setEditingFleetReportName] = useState("");

  // Common state
  const [searchTerm, setSearchTerm] = useState("");
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([
    null,
    null,
  ]);
  const [startDate, endDate] = dateRange;
  const [formattedDateRange, setFormattedDateRange] = useState<{
    startDate: string;
    endDate: string;
  }>({ startDate: "", endDate: "" });
  const [downloadingPdf, setDownloadingPdf] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [fleetPage, setFleetPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [fleetPageSize, setFleetPageSize] = useState(10);
  const [downloadType, setDownloadType] = useState<{ [key: string]: string }>(
    {}
  );
  const [fleetDownloadType, setFleetDownloadType] = useState<{
    [key: string]: string;
  }>({});

  // Tab state
  const [tabValue, setTabValue] = useState(0);

  useEffect(() => {
    setFormattedDateRange({
      startDate: formatDateToYYYYMMDD(startDate),
      endDate: formatDateToYYYYMMDD(endDate),
    });
  }, [startDate, endDate]);

  useEffect(() => {
    if (tabValue === 0) {
      // Load Site Estimator Reports
      dispatch(
        getPastGeneratedReports({
          page,
          limit: pageSize,
          formattedDateRange:
            formattedDateRange.startDate || formattedDateRange.endDate
              ? formattedDateRange
              : undefined,
        })
      );
    } else {
      // Load Fleet Estimator Reports
      dispatch(
        getPastFleetReports({
          page: fleetPage,
          limit: fleetPageSize,
          formattedDateRange:
            formattedDateRange.startDate || formattedDateRange.endDate
              ? formattedDateRange
              : undefined,
        })
      );
    }
  }, [
    dispatch,
    page,
    pageSize,
    fleetPage,
    fleetPageSize,
    formattedDateRange,
    tabValue,
  ]);

  useEffect(() => {
    const hasWLDownload =
      Object.values(downloadType).includes("WL") ||
      Object.values(fleetDownloadType).includes("WL");
    if (hasWLDownload && !whiteLabel && !whiteLabelLoading) {
      dispatch(getMyAddWhiteLabel());
    }
  }, [
    dispatch,
    downloadType,
    fleetDownloadType,
    whiteLabel,
    whiteLabelLoading,
  ]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setPage(1);
    setFleetPage(1);
    setSelectedReports([]);
    setSelectedFleetReports([]);
  };

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setPage(value);
  };

  const handleFleetPageChange = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setFleetPage(value);
  };

  const handlePageSizeChange = (event: any) => {
    setPageSize(event.target.value);
    setPage(1);
  };

  const handleFleetPageSizeChange = (event: any) => {
    setFleetPageSize(event.target.value);
    setFleetPage(1);
  };

  // Site Estimator Report handlers
  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelected = reports.map((report) => report._id);
      setSelectedReports(newSelected);
    } else {
      setSelectedReports([]);
    }
  };

  const handleSelectClick = (
    event: React.ChangeEvent<HTMLInputElement>,
    id: string
  ) => {
    const selectedIndex = selectedReports.indexOf(id);
    let newSelected: string[] = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selectedReports, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selectedReports.slice(1));
    } else if (selectedIndex === selectedReports.length - 1) {
      newSelected = newSelected.concat(selectedReports.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selectedReports.slice(0, selectedIndex),
        selectedReports.slice(selectedIndex + 1)
      );
    }
    setSelectedReports(newSelected);
  };

  const isSelected = (id: string) => selectedReports.indexOf(id) !== -1;

  // Fleet Estimator Report handlers
  const handleSelectAllFleetClick = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (event.target.checked) {
      const newSelected = fleetReports.map((report) => report._id);
      setSelectedFleetReports(newSelected);
    } else {
      setSelectedFleetReports([]);
    }
  };

  const handleSelectFleetClick = (
    event: React.ChangeEvent<HTMLInputElement>,
    id: string
  ) => {
    const selectedIndex = selectedFleetReports.indexOf(id);
    let newSelected: string[] = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selectedFleetReports, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selectedFleetReports.slice(1));
    } else if (selectedIndex === selectedFleetReports.length - 1) {
      newSelected = newSelected.concat(selectedFleetReports.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selectedFleetReports.slice(0, selectedIndex),
        selectedFleetReports.slice(selectedIndex + 1)
      );
    }
    setSelectedFleetReports(newSelected);
  };

  const isFleetSelected = (id: string) =>
    selectedFleetReports.indexOf(id) !== -1;

  const filteredReports = reports.filter((report) => {
    const matchesSearch = report.reportName
      .toLowerCase()
      .includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  const filteredFleetReports = fleetReports.filter((report) => {
    const matchesSearch = report.reportName
      .toLowerCase()
      .includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  // Site Estimator editing
  const startEditing = (reportId: string, currentName: string) => {
    if (loading) return;
    setEditingReportId(reportId);
    setEditingReportName(currentName);
  };

  const cancelEditing = () => {
    setEditingReportId(null);
    setEditingReportName("");
  };

  const saveEditing = async () => {
    if (!editingReportId) return;
    const trimmed = editingReportName.trim();
    const original = reports.find((r) => r._id === editingReportId)?.reportName;
    if (!trimmed || trimmed === original) {
      cancelEditing();
      return;
    }
    try {
      await dispatch(
        updateReportNameById({ id: editingReportId, reportName: trimmed })
      ).unwrap();
      cancelEditing();
    } catch (error) {
      console.error("Failed to update report name:", error);
      cancelEditing();
    }
  };

  // Fleet Estimator editing - COMPLETE IMPLEMENTATION
  const startFleetEditing = (reportId: string, currentName: string) => {
    if (fleetLoading) return;
    setEditingFleetReportId(reportId);
    setEditingFleetReportName(currentName);
  };

  const cancelFleetEditing = () => {
    setEditingFleetReportId(null);
    setEditingFleetReportName("");
  };

  const saveFleetEditing = async () => {
    if (!editingFleetReportId) return;
    const trimmed = editingFleetReportName.trim();
    const original = fleetReports.find(
      (r) => r._id === editingFleetReportId
    )?.reportName;

    if (!trimmed) {
      alert("Report name cannot be empty");
      return;
    }

    if (trimmed === original) {
      cancelFleetEditing();
      return;
    }

    try {
      await dispatch(
        updateFleetReportNameById({
          id: editingFleetReportId,
          reportName: trimmed,
        })
      ).unwrap();

      cancelFleetEditing();
    } catch (error) {
      console.error("Failed to update fleet report name:", error);
    }
  };

  const handleFleetEditKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      saveFleetEditing();
    } else if (e.key === "Escape") {
      e.preventDefault();
      cancelFleetEditing();
    }
  };

  const handleDownloadPdf = async (report: any, downloadType: string) => {
    if (!report) {
      alert("No report data to download.");
      return;
    }

    const isWhiteLabel = downloadType === "WL";
    let whiteLabelConfig: WhiteLabelConfig | undefined;
    if (isWhiteLabel && whiteLabel) {
      whiteLabelConfig = {
        brandLogo: whiteLabel.brandLogo,
        brandColor: whiteLabel.brandColor,
        textColor: whiteLabel.textColor,
        bgColor: whiteLabel.bgColor,
        domainURL: whiteLabel.domainURL || "",
        termsOfServiceURL: whiteLabel.termsOfServiceURL || "",
        privacyPolicyURL: whiteLabel.privacyPolicyURL || "",
      };
    }

    try {
      setDownloadingPdf(report._id);

      // For Site Estimator Reports
      if (tabValue === 0) {
        const details: ReportDetails = {
          reportName: report.reportName,
          netProfit: report.netProfit,
          address: report.location || "",
          financialTable: report.financialTable,
          breakEvenCashFlow: report.breakEvenCashFlow,
          revenueCost: report.revenueCost,
          co2Savings: report.co2Savings,
          utilityDetails: {
            state: report.utilityDetails?.state || "--",
            zipCode: report.utilityDetails?.zipCode || "--",
            utilityName: report.utilityDetails?.utilityName || "--",
            utilityId: report.utilityDetails?.utilityId || "--",
            scheduleId: report.utilityDetails?.scheduleId || "--",
            totalCost: Number(report.utilityDetails?.totalCost) || 0,
          },
          energyCosts: report.energyCosts || {
            energy: Array(5).fill(0),
            energyTime: Array(5).fill(0),
            incrementalEnergy: Array(5).fill(0),
            demand: Array(5).fill(0),
            demandTime: Array(5).fill(0),
            incrementalDemand: Array(5).fill(0),
            serviceCharge: Array(5).fill(0),
            otherCharges: Array(5).fill(0),
            total: Array(5).fill(0),
          },
          grantCostDetails: report.grantCostDetails || [],
          excludeIncentives: report.excludeIncentives || false,
          taxBenefits: {
            applyTaxBenefits: report.applyTaxBenefits || false,
            businessTaxRate: report.businessTaxRate || 21,
            section179Limit: report.section179Limit || 0,
            bonusDepreciationPct: report.bonusDepreciationPct || 0,
            qualifyingSpend: report.qualifyingSpend || 0,
            taxSavings: report.taxSavings || 0,
            upfrontAfterTax: report.upfrontAfterTax || 0,
          },
        };

        await downloadReport(details, isWhiteLabel, whiteLabelConfig, false);
      } else {
        // For Fleet Estimator Reports
        // Extract annual savings from financialTable
        const annualSavingsItem = report.financialTable?.find(
          (item: any) => item.category === "Annual Savings"
        );

        // Parse the annual savings value (remove $ and commas, then convert to number)
        const annualSavingsValue = annualSavingsItem
          ? parseFloat(annualSavingsItem.value.replace(/[$,]/g, ""))
          : 0;

        const details: FleetReportDetails = {
          reportName: report.reportName || "Fleet EV",
          netSavings: report.netSavings,
          annualSavings: annualSavingsValue || 0,
          address: report.location || report.address || "",
          city: report.city || "",
          zipCode: report.zipCode || "",
          fleetSize: report.fleetSize || parseInt(report.numVehicles || "1"),
          analysisYears: report.analysisYears || parseInt(report.years || "7"),
          paybackPeriod: report.paybackPeriod || 0,
          financialTable: report.financialTable || [
            {
              category: "Annual ICE Fuel Cost",
              value: `$${report.iceFuelCostAnnual?.toLocaleString() || "0"}`,
            },
            {
              category: "Annual EV Energy Cost",
              value: `$${report.evEnergyCostAnnual?.toLocaleString() || "0"}`,
            },
            {
              category: "Annual ICE Maintenance",
              value: `$${report.iceMaintAnnual?.toLocaleString() || "0"}`,
            },
            {
              category: "Annual EV Maintenance",
              value: `$${report.evMaintAnnual?.toLocaleString() || "0"}`,
            },
            {
              category: "Annual ICE Opex",
              value: `$${report.iceOpexAnnual?.toLocaleString() || "0"}`,
            },
            {
              category: "Annual EV Opex",
              value: `$${report.evOpexAnnual?.toLocaleString() || "0"}`,
            },
            {
              category: "Annual Savings",
              value: `$${report.annualSavings?.toLocaleString() || "0"}`,
            },
            {
              category: "Upfront EVs Cost",
              value: `$${report.upfrontEVs?.toLocaleString() || "0"}`,
            },
            {
              category: "Incentives Total",
              value: `$${report.incentives?.toLocaleString() || "0"}`,
            },
            {
              category: "Resale Value Total",
              value: `$${report.resaleTotal?.toLocaleString() || "0"}`,
            },
            {
              category: "Upfront Gross",
              value: `$${report.upfrontGross?.toLocaleString() || "0"}`,
            },
            {
              category: "Upfront Net Outlay",
              value: `$${report.upfrontNet?.toLocaleString() || "0"}`,
            },
          ],
          taxBenefitsTable:
            report.taxBenefitsTable ||
            (report.applyTaxBenefits
              ? [
                  {
                    category: "Business Tax Rate",
                    value: `${report.businessTaxRate || 21}%`,
                  },
                  {
                    category: "Section 179 Limit",
                    value: `$${(
                      report.section179Limit || 2500000
                    ).toLocaleString()}`,
                  },
                  {
                    category: "Bonus Depreciation %",
                    value: `${report.bonusDepreciationPct || 0}%`,
                  },
                  {
                    category: "Qualifying Spend",
                    value: `$${(report.qualifyingSpend || 0).toLocaleString()}`,
                  },
                  {
                    category: "Est. Section 179 Deduction",
                    value: `$${(report.est179Deduction || 0).toLocaleString()}`,
                  },
                  {
                    category: "Est. 168(k) Deduction",
                    value: `$${(
                      report.est168kDeduction || 0
                    ).toLocaleString()}`,
                  },
                  {
                    category: "Estimated Tax Savings",
                    value: `$${(report.estTaxSavings || 0).toLocaleString()}`,
                  },
                ]
              : undefined),
          environmentalTable: report.environmentalTable || [
            {
              category: "Annual Gasoline Avoided",
              value: `${report.gallonsAvoidedAnnual?.toLocaleString() || "0"}`,
            },
            {
              category: "Annual CO₂ Offset (Tailpipe)",
              value: `${report.co2AnnualKgTailpipe?.toLocaleString() || "0"}`,
            },
            {
              category: "Annual Grid Emissions",
              value: `${report.gridEmissionsAnnual?.toLocaleString() || "0"}`,
            },
            {
              category: "Annual Net CO2",
              value: `${report.co2AnnualKgNet?.toLocaleString() || "0"}`,
            },
            {
              category: `${report.analysisYears || 7}-Year Net CO2`,
              value: `${(report.co2NetTons || 0).toFixed(2)}`,
            },
          ],
          costOverTimeData: report.costOverTimeData || {
            labels: Array.from(
              { length: (report.analysisYears || 7) + 1 },
              (_, i) => `Year ${i}`
            ),
            datasets: [
              {
                label: "ICE Opex",
                data:
                  report.costOverTimeIce ||
                  Array((report.analysisYears || 7) + 1).fill(0),
              },
              {
                label: "EV Opex",
                data:
                  report.costOverTimeEv ||
                  Array((report.analysisYears || 7) + 1).fill(0),
              },
            ],
          },
          cumulativeSavingsData: report.cumulativeSavingsData || {
            labels: Array.from(
              { length: (report.analysisYears || 7) + 1 },
              (_, i) => `Year ${i}`
            ),
            data:
              report.cumulativeNetSavings ||
              Array((report.analysisYears || 7) + 1).fill(0),
          },
          environmentalData: report.environmentalData || {
            labels: [
              "Gasoline Avoided (gal)",
              "Tailpipe CO2 Avoided (kg)",
              "Net CO2 (kg)",
            ],
            data: [
              report.gallonsAvoidedAnnual || 0,
              report.co2AnnualKgTailpipe || 0,
              report.co2AnnualKgNet || 0,
            ],
          },
          utilityDetails: report.utilityDetails,
          utilityRate: report.utilityRate || 0.14,
          applyTaxBenefits: report.applyTaxBenefits || false,
        };

        await downloadFleetReport(
          details,
          isWhiteLabel,
          whiteLabelConfig,
          false
        );
      }
    } catch (err) {
      alert("Failed to generate PDF. Please try again.");
      console.error("Download error:", err);
    } finally {
      setDownloadingPdf(null);
    }
  };

  const handleDownloadMultiplePdfs = async () => {
    if (tabValue === 0 && selectedReports.length === 0) {
      alert("Please select at least one report to download.");
      return;
    }
    if (tabValue === 1 && selectedFleetReports.length === 0) {
      alert("Please select at least one report to download.");
      return;
    }

    setDownloadingPdf("multiple");

    try {
      let selectedData: Array<{ report: any; type: string }> = [];

      if (tabValue === 0) {
        selectedData = reports
          .filter((report) => selectedReports.includes(report._id))
          .map((report) => ({ report, type: "site" }));
      } else {
        selectedData = fleetReports
          .filter((report) => selectedFleetReports.includes(report._id))
          .map((report) => ({ report, type: "fleet" }));
      }

      const reportPromises = selectedData.map(async ({ report, type }) => {
        if (type === "site") {
          const isWhiteLabel = downloadType[report._id] === "WL";
          let whiteLabelConfig: WhiteLabelConfig | undefined;
          if (isWhiteLabel && whiteLabel) {
            whiteLabelConfig = {
              brandLogo: whiteLabel.brandLogo,
              brandColor: whiteLabel.brandColor,
              textColor: whiteLabel.textColor,
              bgColor: whiteLabel.bgColor,
              domainURL: whiteLabel.domainURL || "",
              termsOfServiceURL: whiteLabel.termsOfServiceURL || "",
              privacyPolicyURL: whiteLabel.privacyPolicyURL || "",
            };
          }

          const details: ReportDetails = {
            reportName: report.reportName,
            netProfit: report.netProfit,
            address: report.location || "",
            financialTable: report.financialTable,
            breakEvenCashFlow: report.breakEvenCashFlow,
            revenueCost: report.revenueCost,
            co2Savings: report.co2Savings,
            utilityDetails: {
              state: report.utilityDetails?.state || "--",
              zipCode: report.utilityDetails?.zipCode || "--",
              utilityName: report.utilityDetails?.utilityName || "--",
              utilityId: report.utilityDetails?.utilityId || "--",
              scheduleId: report.utilityDetails?.scheduleId || "--",
              totalCost: Number(report.utilityDetails?.totalCost) || 0,
            },
            energyCosts: report.energyCosts || {
              energy: Array(5).fill(0),
              energyTime: Array(5).fill(0),
              incrementalEnergy: Array(5).fill(0),
              demand: Array(5).fill(0),
              demandTime: Array(5).fill(0),
              incrementalDemand: Array(5).fill(0),
              serviceCharge: Array(5).fill(0),
              otherCharges: Array(5).fill(0),
              total: Array(5).fill(0),
            },
            grantCostDetails: report.grantCostDetails || [],
            excludeIncentives: report.excludeIncentives || false,
            taxBenefits: {
              applyTaxBenefits: report.applyTaxBenefits || false,
              businessTaxRate: report.businessTaxRate || 21,
              section179Limit: report.section179Limit || 0,
              bonusDepreciationPct: report.bonusDepreciationPct || 0,
              qualifyingSpend: report.qualifyingSpend || 0,
              taxSavings: report.taxSavings || 0,
              upfrontAfterTax: report.upfrontAfterTax || 0,
            },
          };

          const blob = await downloadReport(
            details,
            isWhiteLabel,
            whiteLabelConfig,
            true
          );
          return { blob, report, type: "site" };
        } else {
          // Handle fleet report download
          const isWhiteLabel = fleetDownloadType[report._id] === "WL";
          let whiteLabelConfig: WhiteLabelConfig | undefined;
          if (isWhiteLabel && whiteLabel) {
            whiteLabelConfig = {
              brandLogo: whiteLabel.brandLogo,
              brandColor: whiteLabel.brandColor,
              textColor: whiteLabel.textColor,
              bgColor: whiteLabel.bgColor,
              domainURL: whiteLabel.domainURL || "",
              termsOfServiceURL: whiteLabel.termsOfServiceURL || "",
              privacyPolicyURL: whiteLabel.privacyPolicyURL || "",
            };
          }

          const details: FleetReportDetails = {
            reportName: report.reportName,
            netSavings: report.netSavings,
            annualSavings: report.annualSavings || 0,
            address: report.location || report.address || "",
            city: report.city || "",
            zipCode: report.zipCode || "",
            fleetSize: report.fleetSize || parseInt(report.numVehicles || "1"),
            analysisYears:
              report.analysisYears || parseInt(report.years || "7"),
            paybackPeriod: report.paybackPeriod || 0,
            financialTable: report.financialTable,
            taxBenefitsTable: report.taxBenefitsTable,
            environmentalTable: report.environmentalTable,
            costOverTimeData: report.costOverTimeData,
            cumulativeSavingsData: report.cumulativeSavingsData,
            environmentalData: report.environmentalData,
            utilityDetails: report.utilityDetails,
            utilityRate: report.utilityRate || 0.14,
            applyTaxBenefits: report.applyTaxBenefits || false,
          };

          const blob = await downloadFleetReport(
            details,
            isWhiteLabel,
            whiteLabelConfig,
            true
          );
          return { blob, report, type: "fleet" };
        }
      });

      const reportsWithBlobs = await Promise.all(reportPromises);

      const zip = new JSZip();
      reportsWithBlobs.forEach(({ blob, report, type }) => {
        const safeName = report.reportName.replace(/[^a-z0-9]+/gi, "_");
        const filename = `${safeName}_${
          type === "site" ? "Site" : "Fleet"
        }_Report.pdf`;
        zip.file(filename, blob as Blob);
      });

      const zipBlob = await zip.generateAsync({ type: "blob" });
      const url = URL.createObjectURL(zipBlob as Blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${tabValue === 0 ? "Site" : "Fleet"}_Reports_${
        new Date().toISOString().split("T")[0]
      }.zip`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      alert("Failed to generate ZIP file. Please try again.");
      console.error("ZIP generation error:", err);
    } finally {
      setDownloadingPdf(null);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const handleViewReport = (report: any) => {
    if (tabValue === 0) {
      navigate("/main/generate-reports/view", {
        state: { savedReport: report },
      });
    } else {
      navigate("/main/fleet-reports/view", {
        state: { savedReport: report },
      });
    }
  };

  return (
    <Container>
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2, ease: "easeOut" }}
        style={{
          width: "100%",
          maxWidth: "auto",
          margin: "0 auto",
          paddingBottom: "80px",
        }}
      >
        <Header>
          <WelcomeText>Past Reports</WelcomeText>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: theme.spacing(isTablet ? 1 : 2),
              [theme.breakpoints.down("sm")]: {
                flexDirection: "column",
                width: "100%",
              },
            }}
          >
            <SearchInput
              placeholder="Search reports..."
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
            <DatePickerWrapper>
              <DatePicker
                selectsRange
                startDate={startDate}
                endDate={endDate}
                onChange={(update: [Date | null, Date | null]) => {
                  setDateRange(update);
                }}
                isClearable
                placeholderText="Select date range"
                dateFormat="yyyy:MM:dd"
              />
            </DatePickerWrapper>
            <Box
              sx={{
                display: "flex",
                gap: 1,
                [theme.breakpoints.down("sm")]: {
                  width: "100%",
                },
              }}
            >
              <ExportButton
                variant="contained"
                disabled={
                  (tabValue === 0 && selectedReports.length === 0) ||
                  (tabValue === 1 && selectedFleetReports.length === 0) ||
                  loading ||
                  fleetLoading ||
                  downloadingPdf !== null
                }
                onClick={handleDownloadMultiplePdfs}
              >
                {downloadingPdf === "multiple" ? (
                  <CircularProgress size={20} sx={{ color: "#fff" }} />
                ) : (
                  "Export Report"
                )}
              </ExportButton>
            </Box>
          </Box>
        </Header>

        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            sx={{
              "& .MuiTab-root": {
                color: "#4b5b5d",
                fontWeight: 500,
              },
              "& .MuiTab-root.Mui-selected": {
                color: "#455354",
                fontWeight: 700,
              },
              "& .MuiTabs-indicator": {
                backgroundColor: "#455354",
              },
            }}
          >
            <Tab label="Site Estimator Report" />
            <Tab label="Fleet Estimator Report" />
          </Tabs>
        </Box>

        {/* Site Estimator Reports Tab */}
        <TabPanel value={tabValue} index={0}>
          {loading ? (
            <LoaderContainer>
              <CircularProgress
                size={40}
                thickness={4}
                sx={{
                  color: "#4b5b5d",
                }}
              />
              <LoaderText>Loading site estimator reports...</LoaderText>
            </LoaderContainer>
          ) : error ? (
            <Typography color="error" variant="h6" align="center">
              {error}
            </Typography>
          ) : (
            <Box
              sx={{
                overflowX: "auto",
                "-webkit-overflow-scrolling": "touch",
                scrollbarWidth: "thin",
                "&::-webkit-scrollbar": {
                  height: "8px",
                },
                "&::-webkit-scrollbar-track": {
                  background: "#f1f1f1",
                },
                "&::-webkit-scrollbar-thumb": {
                  background: "#4b5b5d",
                  borderRadius: "4px",
                },
                "&::-webkit-scrollbar-thumb:hover": {
                  background: "#354142",
                },
              }}
            >
              <StyledTable size={isMobile ? "small" : "medium"}>
                <TableHead sx={{ background: "#f5f0e7" }}>
                  <TableRow>
                    <StyledTableHeadCell padding="checkbox">
                      <Checkbox
                        sx={{
                          color: "#4b5b5d",
                          "&.Mui-checked": {
                            color: "#4b5b5d",
                          },
                        }}
                        size="small"
                        indeterminate={
                          selectedReports.length > 0 &&
                          selectedReports.length < reports.length
                        }
                        checked={
                          reports.length > 0 &&
                          selectedReports.length === reports.length
                        }
                        onChange={handleSelectAllClick}
                        disabled={loading}
                      />
                    </StyledTableHeadCell>
                    <StyledTableHeadCell>Report Name</StyledTableHeadCell>
                    <StyledTableHeadCell>Net Profit</StyledTableHeadCell>
                    <StyledTableHeadCell>Generated On</StyledTableHeadCell>
                    <StyledTableHeadCell>Actions</StyledTableHeadCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredReports.length === 0 ? (
                    <TableRow>
                      <StyledTableCell
                        colSpan={5}
                        align="center"
                        sx={{ py: 3 }}
                      >
                        <Typography
                          variant="h6"
                          color="#4b5b5d"
                          fontWeight={500}
                        >
                          No Data Available
                        </Typography>
                      </StyledTableCell>
                    </TableRow>
                  ) : (
                    filteredReports.map((report) => {
                      const truncatedReportName =
                        report.reportName.length > 40
                          ? `${report.reportName.slice(0, 40)}...`
                          : report.reportName;
                      const isItemSelected = isSelected(report._id);
                      return (
                        <TableRow
                          key={report._id}
                          selected={isItemSelected}
                          sx={{
                            "&:nth-of-type(odd)": {
                              backgroundColor: "#f8f8f8",
                            },
                            "&:hover": { backgroundColor: "#e0e0e0" },
                          }}
                        >
                          <StyledTableCell padding="checkbox">
                            <Checkbox
                              size="small"
                              checked={isItemSelected}
                              onChange={(event) =>
                                handleSelectClick(event, report._id)
                              }
                              sx={{
                                color: "#4b5b5d",
                                "&.Mui-checked": {
                                  color: "#4b5b5d",
                                },
                              }}
                              disabled={loading}
                            />
                          </StyledTableCell>
                          <StyledTableCell
                            onDoubleClick={() =>
                              startEditing(report._id, report.reportName)
                            }
                            sx={{ cursor: "text" }}
                          >
                            {editingReportId === report._id ? (
                              <TextField
                                value={editingReportName}
                                size="small"
                                autoFocus
                                onChange={(e) =>
                                  setEditingReportName(e.target.value)
                                }
                                sx={{
                                  "& .MuiOutlinedInput-input": {
                                    padding: "4px 8px",
                                    fontSize: "0.8rem",
                                  },
                                }}
                                onBlur={saveEditing}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") {
                                    e.preventDefault();
                                    saveEditing();
                                  } else if (e.key === "Escape") {
                                    e.preventDefault();
                                    cancelEditing();
                                  }
                                }}
                                variant="outlined"
                                fullWidth
                              />
                            ) : (
                              truncatedReportName
                            )}
                          </StyledTableCell>
                          <StyledTableCell>
                            {"$"}
                            {report.netProfit?.toLocaleString()}
                          </StyledTableCell>
                          <StyledTableCell>
                            {formatDate(report.createdAt)}
                          </StyledTableCell>
                          <StyledTableCell>
                            <Box
                              sx={{
                                display: "flex",
                                gap: isMobile ? 1 : 0,
                                flexDirection: "row",
                                alignItems: "center",
                              }}
                            >
                              <Tooltip title="View Report">
                                <IconButton
                                  size={isMobile ? "small" : "medium"}
                                  onClick={() => handleViewReport(report)}
                                  disabled={loading}
                                  sx={{ color: "#4b5b5d" }}
                                >
                                  <VisibilityIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Download PDF">
                                <IconButton
                                  size={isMobile ? "small" : "medium"}
                                  onClick={() =>
                                    handleDownloadPdf(
                                      report,
                                      downloadType[report._id] || "PDF"
                                    )
                                  }
                                  disabled={
                                    loading || downloadingPdf === report._id
                                  }
                                  sx={{ color: "#4b5b5d" }}
                                >
                                  {downloadingPdf === report._id ? (
                                    <CircularProgress
                                      size={16}
                                      sx={{ color: "#4b5b5d" }}
                                    />
                                  ) : (
                                    <ArrowDownwardIcon fontSize="small" />
                                  )}
                                </IconButton>
                              </Tooltip>
                              {!isStarterPlan && (
                                <DownloadSelect
                                  value={downloadType[report._id] || "PDF"}
                                  onChange={(e) =>
                                    setDownloadType({
                                      ...downloadType,
                                      [report._id]: e.target.value as string,
                                    })
                                  }
                                  disabled={
                                    loading || downloadingPdf === report._id
                                  }
                                >
                                  <MenuItem
                                    value="PDF"
                                    sx={{ fontSize: "0.65rem" }}
                                  >
                                    PDF
                                  </MenuItem>
                                  <MenuItem
                                    value="WL"
                                    sx={{ fontSize: "0.65rem" }}
                                  >
                                    WL PDF
                                  </MenuItem>
                                </DownloadSelect>
                              )}
                            </Box>
                          </StyledTableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </StyledTable>
            </Box>
          )}
        </TabPanel>

        {/* Fleet Estimator Reports Tab */}
        <TabPanel value={tabValue} index={1}>
          {fleetLoading ? (
            <LoaderContainer>
              <CircularProgress
                size={40}
                thickness={4}
                sx={{
                  color: "#4b5b5d",
                }}
              />
              <LoaderText>Loading fleet estimator reports...</LoaderText>
            </LoaderContainer>
          ) : fleetError ? (
            <Typography color="error" variant="h6" align="center">
              {fleetError}
            </Typography>
          ) : (
            <Box
              sx={{
                overflowX: "auto",
                "-webkit-overflow-scrolling": "touch",
                scrollbarWidth: "thin",
                "&::-webkit-scrollbar": {
                  height: "8px",
                },
                "&::-webkit-scrollbar-track": {
                  background: "#f1f1f1",
                },
                "&::-webkit-scrollbar-thumb": {
                  background: "#4b5b5d",
                  borderRadius: "4px",
                },
                "&::-webkit-scrollbar-thumb:hover": {
                  background: "#354142",
                },
              }}
            >
              <StyledTable size={isMobile ? "small" : "medium"}>
                <TableHead sx={{ background: "#f5f0e7" }}>
                  <TableRow>
                    <StyledTableHeadCell padding="checkbox">
                      <Checkbox
                        sx={{
                          color: "#4b5b5d",
                          "&.Mui-checked": {
                            color: "#4b5b5d",
                          },
                        }}
                        size="small"
                        indeterminate={
                          selectedFleetReports.length > 0 &&
                          selectedFleetReports.length < fleetReports.length
                        }
                        checked={
                          fleetReports.length > 0 &&
                          selectedFleetReports.length === fleetReports.length
                        }
                        onChange={handleSelectAllFleetClick}
                        disabled={fleetLoading}
                      />
                    </StyledTableHeadCell>
                    <StyledTableHeadCell>Report Name</StyledTableHeadCell>
                    <StyledTableHeadCell>Net Savings</StyledTableHeadCell>
                    <StyledTableHeadCell>Generated On</StyledTableHeadCell>
                    <StyledTableHeadCell>Actions</StyledTableHeadCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredFleetReports.length === 0 ? (
                    <TableRow>
                      <StyledTableCell
                        colSpan={5}
                        align="center"
                        sx={{ py: 3 }}
                      >
                        <Typography
                          variant="h6"
                          color="#4b5b5d"
                          fontWeight={500}
                        >
                          No Data Available
                        </Typography>
                      </StyledTableCell>
                    </TableRow>
                  ) : (
                    filteredFleetReports.map((report) => {
                      const truncatedReportName =
                        report.reportName.length > 40
                          ? `${report.reportName.slice(0, 40)}...`
                          : report.reportName;
                      const isItemSelected = isFleetSelected(report._id);
                      return (
                        <TableRow
                          key={report._id}
                          selected={isItemSelected}
                          sx={{
                            "&:nth-of-type(odd)": {
                              backgroundColor: "#f8f8f8",
                            },
                            "&:hover": { backgroundColor: "#e0e0e0" },
                          }}
                        >
                          <StyledTableCell padding="checkbox">
                            <Checkbox
                              size="small"
                              checked={isItemSelected}
                              onChange={(event) =>
                                handleSelectFleetClick(event, report._id)
                              }
                              sx={{
                                color: "#4b5b5d",
                                "&.Mui-checked": {
                                  color: "#4b5b5d",
                                },
                              }}
                              disabled={fleetLoading}
                            />
                          </StyledTableCell>
                          <StyledTableCell
                            onDoubleClick={() =>
                              startFleetEditing(report._id, report.reportName)
                            }
                            sx={{ cursor: "text" }}
                          >
                            {editingFleetReportId === report._id ? (
                              <TextField
                                value={editingFleetReportName}
                                size="small"
                                autoFocus
                                onChange={(e) =>
                                  setEditingFleetReportName(e.target.value)
                                }
                                onBlur={saveFleetEditing}
                                onKeyDown={handleFleetEditKeyDown}
                                variant="outlined"
                                fullWidth
                                sx={{
                                  "& .MuiOutlinedInput-input": {
                                    padding: "4px 8px",
                                    fontSize: "0.8rem",
                                  },
                                }}
                              />
                            ) : (
                              truncatedReportName
                            )}
                          </StyledTableCell>
                          <StyledTableCell>
                            {"$"}
                            {report.netSavings?.toLocaleString()}
                          </StyledTableCell>
                          <StyledTableCell>
                            {formatDate(report.createdAt)}
                          </StyledTableCell>
                          <StyledTableCell>
                            <Box
                              sx={{
                                display: "flex",
                                gap: isMobile ? 1 : 0,
                                flexDirection: "row",
                                alignItems: "center",
                              }}
                            >
                              <Tooltip title="View Report">
                                <IconButton
                                  size={isMobile ? "small" : "medium"}
                                  onClick={() => handleViewReport(report)}
                                  disabled={fleetLoading}
                                  sx={{ color: "#4b5b5d" }}
                                >
                                  <VisibilityIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Download PDF">
                                <IconButton
                                  size={isMobile ? "small" : "medium"}
                                  onClick={() =>
                                    handleDownloadPdf(
                                      report,
                                      fleetDownloadType[report._id] || "PDF"
                                    )
                                  }
                                  disabled={
                                    fleetLoading ||
                                    downloadingPdf === report._id
                                  }
                                  sx={{ color: "#4b5b5d" }}
                                >
                                  {downloadingPdf === report._id ? (
                                    <CircularProgress
                                      size={16}
                                      sx={{ color: "#4b5b5d" }}
                                    />
                                  ) : (
                                    <ArrowDownwardIcon fontSize="small" />
                                  )}
                                </IconButton>
                              </Tooltip>
                              {!isStarterPlan && (
                                <DownloadSelect
                                  value={fleetDownloadType[report._id] || "PDF"}
                                  onChange={(e) =>
                                    setFleetDownloadType({
                                      ...fleetDownloadType,
                                      [report._id]: e.target.value as string,
                                    })
                                  }
                                  disabled={
                                    fleetLoading ||
                                    downloadingPdf === report._id
                                  }
                                >
                                  <MenuItem
                                    value="PDF"
                                    sx={{ fontSize: "0.65rem" }}
                                  >
                                    PDF
                                  </MenuItem>
                                  <MenuItem
                                    value="WL"
                                    sx={{ fontSize: "0.65rem" }}
                                  >
                                    WL PDF
                                  </MenuItem>
                                </DownloadSelect>
                              )}
                            </Box>
                          </StyledTableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </StyledTable>
            </Box>
          )}
        </TabPanel>

        {/* Pagination for Site Estimator Reports */}
        {tabValue === 0 && !loading && (
          <FooterContainer>
            <Typography
              variant="body2"
              color="#4b5b5d"
              sx={{
                display: "flex",
                justifyContent: isMobile ? "center" : "flex-end",
                width: isMobile ? "100%" : "auto",
              }}
            >
              Showing {(page - 1) * pageSize + 1} to{" "}
              {Math.min(page * pageSize, totalReports)} of {totalReports}{" "}
              entries
            </Typography>

            {!isMobile && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: 2,
                }}
              >
                <Typography variant="body2" color="#4b5b5d">
                  Show:
                </Typography>
                <FormControl size="small" sx={{ minWidth: 80 }}>
                  <Select
                    value={pageSize}
                    onChange={handlePageSizeChange}
                    sx={{
                      "& .MuiSelect-select": {
                        padding: "4px 8px",
                        fontSize: "0.8rem",
                      },
                    }}
                  >
                    <MenuItem value={10}>10</MenuItem>
                    <MenuItem value={20}>20</MenuItem>
                    <MenuItem value={50}>50</MenuItem>
                  </Select>
                </FormControl>
                <Typography variant="body2" color="#4b5b5d">
                  entries
                </Typography>
              </Box>
            )}

            <Pagination
              count={totalPages}
              page={page}
              onChange={handlePageChange}
              color="primary"
              size={isMobile ? "small" : "medium"}
              showFirstButton
              showLastButton
              sx={{
                display: "flex",
                justifyContent: isMobile ? "center" : "flex-end",
                width: isMobile ? "100%" : "auto",
                "& .MuiPaginationItem-root": {
                  color: "#4b5b5d",
                  "&.Mui-selected": {
                    backgroundColor: "#758d88",
                    color: "#f8f9fa",
                    fontWeight: 600,
                    "&:hover": {
                      backgroundColor: "#4b5b5d",
                    },
                  },
                  "&:hover": {
                    backgroundColor: "rgba(75, 91, 93, 0.04)",
                  },
                },
              }}
            />
          </FooterContainer>
        )}

        {/* Pagination for Fleet Estimator Reports */}
        {tabValue === 1 && !fleetLoading && (
          <FooterContainer>
            <Typography
              variant="body2"
              color="#4b5b5d"
              sx={{
                display: "flex",
                justifyContent: isMobile ? "center" : "flex-end",
                width: isMobile ? "100%" : "auto",
              }}
            >
              Showing {(fleetPage - 1) * fleetPageSize + 1} to{" "}
              {Math.min(fleetPage * fleetPageSize, fleetTotalReports)} of{" "}
              {fleetTotalReports} entries
            </Typography>

            {!isMobile && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: 2,
                }}
              >
                <Typography variant="body2" color="#4b5b5d">
                  Show:
                </Typography>
                <FormControl size="small" sx={{ minWidth: 80 }}>
                  <Select
                    value={fleetPageSize}
                    onChange={handleFleetPageSizeChange}
                    sx={{
                      "& .MuiSelect-select": {
                        padding: "4px 8px",
                        fontSize: "0.8rem",
                      },
                    }}
                  >
                    <MenuItem value={10}>10</MenuItem>
                    <MenuItem value={20}>20</MenuItem>
                    <MenuItem value={50}>50</MenuItem>
                  </Select>
                </FormControl>
                <Typography variant="body2" color="#4b5b5d">
                  entries
                </Typography>
              </Box>
            )}

            <Pagination
              count={fleetTotalPages}
              page={fleetPage}
              onChange={handleFleetPageChange}
              color="primary"
              size={isMobile ? "small" : "medium"}
              showFirstButton
              showLastButton
              sx={{
                display: "flex",
                justifyContent: isMobile ? "center" : "flex-end",
                width: isMobile ? "100%" : "auto",
                "& .MuiPaginationItem-root": {
                  color: "#4b5b5d",
                  "&.Mui-selected": {
                    backgroundColor: "#758d88",
                    color: "#f8f9fa",
                    fontWeight: 600,
                    "&:hover": {
                      backgroundColor: "#4b5b5d",
                    },
                  },
                  "&:hover": {
                    backgroundColor: "rgba(75, 91, 93, 0.04)",
                  },
                },
              }}
            />
          </FooterContainer>
        )}
      </motion.div>
    </Container>
  );
};

export default PastReports;
